import { defineStore } from 'pinia'
import { ref } from 'vue'
import UniDataTable from '@/common/classes/UniDataTable'

/** Элемент списка файлов для отображения в диалоге (id, name) + сырые данные строки для возврата */
export interface FileSelectItem {
  id: string
  name: string
  /** Исходные данные строки row (fields) для формирования результата UniDataTable */
  rawRow: Record<string, unknown>
}

/** Преобразует UniDataTable в FileSelectItem[] для диалога выбора файлов */
function normalizeFilesToItems(files: UniDataTable): FileSelectItem[] {
  if (!files || files.size() === 0) {
    return []
  }
  const items: FileSelectItem[] = []
  for (let i = 0; i < files.size(); i++) {
    const row = files.getRow(i)
    // TODO: если присутствует getField, то получать значения через него!
    const id = String(row.fields?.code.value ?? `row-${i}`)
    const name = row.fields?.name.value ?? row.fields?.fileName.value ?? 'no name'
    items.push({
      id,
      name,
      rawRow: { fields: row.fields ?? {} }
    })
  }
  return items
}

export const useFileSelectModal = defineStore('fileSelectModal', () => {
  const isOpen = ref(false)
  const files = ref<FileSelectItem[]>([])
  const selectedIds = ref<Set<string>>(new Set())

  let resolveSelection: ((selected: FileSelectItem[]) => void) | null = null

  /**
   * Открывает диалог выбора файлов.
   * @param filesTable — UniDataTable со строками вложений
   * @returns Promise со списком выбранных пользователем файлов (FileSelectItem[]) или пустой массив при отмене
   */
  function openForFileSelection(filesTable: UniDataTable): Promise<FileSelectItem[]> {
    const items = normalizeFilesToItems(filesTable)
    // console.log('for select files', items)
    files.value = items
    selectedIds.value = new Set(items.map((f) => f.id))
    isOpen.value = true
    return new Promise<FileSelectItem[]>((resolve) => {
      resolveSelection = resolve
    })
  }

  function isSelected(id: string): boolean {
    return selectedIds.value.has(id)
  }

  function toggle(id: string) {
    const next = new Set(selectedIds.value)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    selectedIds.value = next
  }

  function selectAll() {
    selectedIds.value = new Set(files.value.map((f) => f.id))
  }

  function deselectAll() {
    selectedIds.value = new Set()
  }

  function confirmSelection() {
    const selected = files.value.filter((f) => selectedIds.value.has(f.id))
    if (resolveSelection) {
      resolveSelection(selected)
      resolveSelection = null
    }
    close()
  }

  function cancelSelection() {
    if (resolveSelection) {
      resolveSelection([])
      resolveSelection = null
    }
    close()
  }

  function close() {
    isOpen.value = false
    files.value = []
    selectedIds.value = new Set()
    if (resolveSelection) {
      resolveSelection([])
      resolveSelection = null
    }
  }

  return {
    isOpen,
    files,
    selectedIds,
    openForFileSelection,
    isSelected,
    toggle,
    selectAll,
    deselectAll,
    confirmSelection,
    cancelSelection,
    close,
  }
})
